import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Stack,
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { getSystemHealth } from "api/systemHealthApi";
import { unwrapObject } from "api/unwrap";

/**
 * /admin/system-health — in-app snapshot of the prod stack so operators
 * don't have to leave the app for casual checks. Single backend call,
 * three sections: build/deploy, schedulers, assignment funnel counters.
 *
 * For time-series + alerting → Grafana Cloud (see backend STATUS-LOG entry
 * 2026-05-27 for credentials + dashboard).
 */

const STATUS_COLORS = {
  ok: { bg: "#dcfce7", fg: "#166534" },
  error: { bg: "#fee2e2", fg: "#991b1b" },
};

// Per-scheduler staleness thresholds (in hours). Mirrors the backend
// `scheduler.health.watchlist` default. If you add a watched scheduler
// server-side, also add it here so the UI color-codes correctly.
const STALENESS_HOURS = {
  ride_overage_reporter: 48,
  plan_capacity_nudge: 30,
  trial_winback: 30,
  training_renewal_reminder: 30,
  document_expiry_reminder: 30,
  admin_compliance_digest: 30,
  driver_compliance_digest: 180,
  onboarding_stuck_state: 180,
  outbox_dispatcher: 1,
  outbox_alert: 2,
  retention: 30,
  policy_expiration: 3,
  training_expiration: 3,
  pricing_page_sync_check: 30,
};

function ageHours(ms) {
  return ms / (1000 * 60 * 60);
}

function fmtAge(ms) {
  if (ms < 60_000) return `${Math.round(ms / 1000)}s ago`;
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)}h ago`;
  return `${Math.round(ms / 86_400_000)}d ago`;
}

function schedulerHealthColor(name, ageMs, status) {
  if (status === "error") return "#dc2626";
  const thresholdH = STALENESS_HOURS[name];
  if (thresholdH == null) return "#64748b"; // unwatched — neutral
  const ageH = ageHours(ageMs);
  if (ageH > thresholdH) return "#dc2626"; // overdue
  if (ageH > thresholdH * 0.75) return "#f59e0b"; // approaching
  return "#16a34a";
}

export const SystemHealthPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getSystemHealth();
      setData(unwrapObject(res));
    } catch (e) {
      setError(e?.message || "Failed to load system health");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Auto-refresh every 30s — light load (single endpoint, all in-process).
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);

  const funnelDrops = useMemo(() => {
    const f = data?.assignmentFunnel;
    if (!f) return null;
    const attempted = f.attempted || 0;
    return {
      assignedToNotification: attempted > 0
        ? Math.round(((f.committed || 0) / attempted) * 100)
        : null,
      notificationToAccept: (f.notification_sent || 0) > 0
        ? Math.round(((f.driver_accepted || 0) / f.notification_sent) * 100)
        : null,
      attemptedToAccept: attempted > 0
        ? Math.round(((f.driver_accepted || 0) / attempted) * 100)
        : null,
    };
  }, [data]);

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack
      spacing={3}
      sx={{ p: { xs: 2, md: 3 }, backgroundColor: "#f8fafc", minHeight: "100%" }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "#0f172a" }}>
            System Health
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Backend snapshot — schedulers, deploy version, ride assignment funnel.
            Auto-refreshes every 30 seconds. For trends and alerts use Grafana Cloud.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {error ? <Alert severity="error" onClose={() => setError(null)}>{error}</Alert> : null}

      {/* Build info */}
      {data?.build ? (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="overline" color="text.secondary">Deploy</Typography>
            <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap", mt: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">Environment</Typography>
                <Typography variant="body1" sx={{ fontFamily: "monospace" }}>{data.build.environment}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Version</Typography>
                <Tooltip title={data.build.version}>
                  <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                    {String(data.build.version).slice(0, 12)}
                  </Typography>
                </Tooltip>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Built</Typography>
                <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                  {data.build.buildTime}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Server time</Typography>
                <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                  {new Date(data.build.serverNowMs).toISOString()}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      ) : null}

      {/* Assignment funnel */}
      {data?.assignmentFunnel ? (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="overline" color="text.secondary">Assignment funnel (cumulative since process start)</Typography>
            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mt: 1.5 }}>
              <FunnelStat label="Attempted" value={data.assignmentFunnel.attempted} />
              <FunnelStat label="Committed" value={data.assignmentFunnel.committed} subtle={funnelDrops?.assignedToNotification != null ? `${funnelDrops.assignedToNotification}% of attempted` : null} />
              <FunnelStat label="Notification sent" value={data.assignmentFunnel.notification_sent} />
              <FunnelStat label="Driver accepted" value={data.assignmentFunnel.driver_accepted} subtle={funnelDrops?.attemptedToAccept != null ? `${funnelDrops.attemptedToAccept}% of attempted` : null} />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
              Drops between adjacent stages → ghost-assignment / FCM-failure / driver-action-lag indicators.
            </Typography>
          </CardContent>
        </Card>
      ) : null}

      {/* Outbox snapshot */}
      {data?.outbox ? (
        <Card variant="outlined">
          <CardContent>
            <Typography variant="overline" color="text.secondary">
              Outbox (cross-tenant)
            </Typography>
            <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mt: 1.5 }}>
              <FunnelStat label="Pending" value={data.outbox.pending} />
              <FunnelStat
                label="Failed (DLQ)"
                value={data.outbox.failed}
                subtle={data.outbox.failedLast24h > 0 ? `${data.outbox.failedLast24h} in last 24h` : null}
              />
              <FunnelStat label="Processed (since boot)" value={Math.round(data.outbox.processedTotal || 0)} />
              <FunnelStat label="Dead-lettered (since boot)" value={Math.round(data.outbox.deadletteredTotal || 0)} />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
              Pending climbing or Failed non-zero → dispatcher stuck or an event class is broken end-to-end. See <a href="/admin/outbox">/admin/outbox</a> to inspect.
            </Typography>
          </CardContent>
        </Card>
      ) : null}

      {/* Schedulers */}
      {data?.schedulers ? (
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="overline" color="text.secondary">
                Schedulers ({data.schedulers.length} tracked)
              </Typography>
            </Box>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Last run</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Last metadata</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.schedulers.length === 0 ? (
                    <TableRow><TableCell colSpan={4} sx={{ textAlign: "center", py: 3, color: "#64748b" }}>
                      No scheduler heartbeats recorded yet. Either nothing has fired since deploy, or the watchdog isn't writing.
                    </TableCell></TableRow>
                  ) : data.schedulers.map((s) => {
                    const c = schedulerHealthColor(s.name, s.ageMs, s.status);
                    return (
                      <TableRow key={s.name}>
                        <TableCell sx={{ fontFamily: "monospace" }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: c }} />
                            {s.name}
                          </Box>
                        </TableCell>
                        <TableCell>{fmtAge(s.ageMs)}</TableCell>
                        <TableCell>
                          <Chip
                            label={s.status}
                            size="small"
                            sx={{
                              backgroundColor: STATUS_COLORS[s.status]?.bg || "#e2e8f0",
                              color: STATUS_COLORS[s.status]?.fg || "#475569",
                              fontWeight: 600,
                              height: 20,
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontFamily: "monospace", fontSize: "0.75rem", maxWidth: 400, wordBreak: "break-all" }}>
                          {s.metadata || "—"}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      ) : null}

      {/* Footer link to Grafana */}
      <Box sx={{ textAlign: "center", py: 1 }}>
        <Typography variant="caption" color="text.secondary">
          For time-series, alerting, and historical trends →{" "}
          <a
            href="https://kericocare.grafana.net/"
            target="_blank"
            rel="noreferrer"
            style={{ color: "#0ea5e9" }}
          >
            Grafana Cloud <OpenInNewIcon sx={{ fontSize: 12, verticalAlign: "middle" }} />
          </a>
        </Typography>
      </Box>
    </Stack>
  );
};

const FunnelStat = ({ label, value, subtle }) => (
  <Box sx={{ minWidth: 120 }}>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="h4" sx={{ fontWeight: 700, color: "#0f172a" }}>
      {value != null ? value.toLocaleString() : "—"}
    </Typography>
    {subtle ? (
      <Typography variant="caption" color="text.secondary">{subtle}</Typography>
    ) : null}
  </Box>
);

export default SystemHealthPage;
